<template>
  <v-dialog :value="isOpened"
            :max-width="maxWidth"
            :persistent="persistent"
            scrollable
            @change="onClose">
    <v-card class="reg-dialog">
      <v-toolbar flat color="grey lighten-4">
        <v-icon color="primary" class="mr-2">
          {{ icon }}
        </v-icon>
        <v-toolbar-title class="text-h6">
          {{ title }}
        </v-toolbar-title>
        <v-spacer />
        <v-btn icon @click="close">
          <v-icon>close</v-icon>
        </v-btn>
      </v-toolbar>
      <v-divider />
      <v-card-text>
        <v-row>
          <v-col cols="12">
            <reg-dynamic-form ref="form"
                              v-model="formValue"
                              :fields="fields"
                              @save="onSaveClick()" />
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <reg-dynamic-buttons :buttons="dialogButtons"
                             :scope="formValue"
                             align="right"
                             @click="(payload) => $emit('buttonClick', {button: payload.button, item: payload.scope})" />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import _ from 'lodash'
import DynamicForm from '~shared/dynamic/DynamicForm.vue'
import DynamicButtons from '~shared/dynamic/DynamicButtons.vue'

export default {
  name: 'RegDynamicDialog',
  components: {
    'reg-dynamic-form': DynamicForm,
    'reg-dynamic-buttons': DynamicButtons
  },
  props: {
    value: {
      required: false,
      type: Object,
      default: () => {
        return {}
      }
    },
    title: {
      type: String,
      default: ''
    },
    fields: {
      type: Array,
      default: () => []
    },
    icon: {
      type: String,
      default: 'keyboard'
    },
    maxWidth: {
      type: String,
      default: '450px'
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    persistent: {
      type: Boolean,
      default: true
    },
    buttons: {
      type: Array,
      default: () => []
    },
    saveLabel: {
      type: String,
      default: ''
    },
    saveIcon: {
      type: String,
      default: 'mdi-content-save'
    },
    validationFunction: {
      type: Function,
      default: () => {
        return () => true
      }
    }
  },
  data() {
    return {
      isOpened: false,
      formValue: {}
    }
  },
  computed: {
    dialogButtons() {
      if (this.buttons.length === 0) {
        return [
          {
            buttonLabel: this.$t('cancel'),
            flat: true,
            click: this.close,
            large: true,
            visible: !this.readOnly
          },
          {
            buttonLabel: this.saveLabel || this.$t('save'),
            depressed: true,
            icon: this.saveIcon,
            large: true,
            color: 'primary',
            click: this.onSaveClick,
            visible: !this.readOnly
          },
          {
            buttonLabel: this.$t('close'),
            large: true,
            flat: true,
            click: this.close,
            visible: this.readOnly
          }
        ]
      }

      return this.buttons
    }
  },
  watch: {
    value(newVal) {
      this.formValue = _.cloneDeep(newVal)
    }
  },
  created() {
    this.formValue = this.value ? _.cloneDeep(this.value) : {}
  },
  methods: {
    clearForm() {
      if (this.$refs.form) {
        this.$refs.form.reset()
      }
    },
    setFocus(index) {
      const field = this.$refs.form.$el.querySelector('[tabindex="' + index + '"]')

      if (field) {
        field.scrollIntoView({
          block: 'start',
          behavior: 'smooth'
        })

        const fieldInput = field.getElementsByTagName('input')
        if (fieldInput.length) {
          fieldInput[fieldInput.length - 1].select()
        }
      }
    },
    open() {
      this.clearForm()
      this.isOpened = true
      this.formValue = this.value ? _.cloneDeep(this.value) : {}
      document.body.addEventListener('keyup', this.detectKeyPressed)

      this.$nextTick(() => {
        this.setFocus(0)
      })

      this.$emit('open')
    },
    close() {
      this.isOpened = false
      this.$emit('close')
    },
    onClose(val) {
      if (val === false) {
        document.body.removeEventListener('keyup', this.detectKeyPressed)
      }
    },
    async onSaveClick() {
      if (!await this.$refs.form.validate()) {
        return
      }

      if (!this.validationFunction(this.formValue)) {
        return
      }

      this.$emit('save', this.formValue)
      this.close()
    },
    detectKeyPressed(event) {
      if (event.keyCode === 27) {
        this.close()
      } else if (event.keyCode === 13) {
        this.onSaveClick()
      }
    }
  }
}
</script>
